import type { ComponentChildren, RefObject } from 'preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { Check, ChevronDown, Search, X } from 'lucide-preact';
import { cn } from '@/lib/cn';
import { Popover } from '../overlays/Popover';
import { Icon } from './Icon';

export interface SelectOption<T extends string = string> {
  value: T;
  label: ComponentChildren;
  description?: ComponentChildren;
  group?: string;
  disabled?: boolean;
  /** plain text for matching against the search query (defaults to `String(label)`) */
  searchText?: string;
}

export type SelectState = 'default' | 'success' | 'error';

export interface SelectProps<T extends string = string> {
  value: T | null;
  options: SelectOption<T>[];
  onChange: (value: T | null) => void;
  placeholder?: string;
  searchable?: boolean;
  clearable?: boolean;
  disabled?: boolean;
  invalid?: boolean;
  state?: SelectState;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  emptyMessage?: ComponentChildren;
  'aria-label'?: string;
}

const TRIGGER_BASE =
  'flex min-w-0 w-full items-center gap-2 rounded-md border bg-surface text-fg text-left transition-[border-color,box-shadow,background] duration-med ease-snappy focus-within:outline-none disabled:opacity-60 disabled:cursor-not-allowed disabled:pointer-events-none cursor-pointer';

const STATE: Record<SelectState, string> = {
  default:
    'border-border hover:border-border-strong focus-within:border-brand focus-within:shadow-focus',
  success:
    'border-ok hover:border-ok focus-within:border-ok focus-within:shadow-focus-ok',
  error:
    'border-error hover:border-error focus-within:border-error focus-within:shadow-focus-error',
};

const SIZE: Record<NonNullable<SelectProps['size']>, string> = {
  sm: 'h-8 px-3 text-xs',
  md: 'h-9 px-3.5 text-13',
  lg: 'h-12 px-4 text-base',
};

export function Select<T extends string = string>({
  value,
  options,
  onChange,
  placeholder = 'Selecciona…',
  searchable = false,
  clearable = false,
  disabled = false,
  invalid = false,
  state,
  size = 'md',
  className,
  emptyMessage = 'Sin resultados',
  'aria-label': ariaLabel,
}: SelectProps<T>) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const selected = options.find((o) => o.value === value);
  const resolvedState = invalid ? 'error' : state ?? 'default';
  const st = STATE[resolvedState];

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => {
      const text = (o.searchText ?? String(o.label ?? '') + ' ' + (o.description ?? '')).toLowerCase();
      return text.includes(q);
    });
  }, [query, options]);

  const sections = useMemo(() => {
    const map = new Map<string, SelectOption<T>[]>();
    for (const opt of filtered) {
      const key = opt.group ?? '';
      const arr = map.get(key) ?? [];
      arr.push(opt);
      map.set(key, arr);
    }
    return Array.from(map.entries()).map(([group, items]) => ({ group, items }));
  }, [filtered]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-invalid={resolvedState === 'error'}
        aria-label={ariaLabel}
        onClick={() => setOpen((p) => !p)}
        className={cn(
          TRIGGER_BASE,
          SIZE[size],
          st,
          className,
        )}
      >
        <span className={cn('flex-1 truncate', !selected && 'text-fg-faint')}>
          {selected ? selected.label : placeholder}
        </span>
        {clearable && selected && !disabled ? (
          <span
            role="button"
            tabIndex={-1}
            aria-label="Limpiar"
            className="rounded p-0.5 text-fg-faint hover:text-fg hover:bg-surface-2"
            onClick={(e) => {
              e.stopPropagation();
              onChange(null);
            }}
          >
            <X size={12} />
          </span>
        ) : null}
        <Icon as={ChevronDown} size={size === 'sm' ? 14 : 16} className={cn('text-fg-faint transition-transform duration-med', open && 'rotate-180')} />
      </button>

      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchor={triggerRef as RefObject<HTMLElement>}
        placement="bottom-start"
        matchAnchorWidth
        role="listbox"
        className="rounded-md border border-border-subtle bg-surface-elevated p-1 shadow-md"
      >
        {searchable ? (
          <div className="flex items-center gap-2 border-b border-border-subtle px-3 py-2">
            <Search size={14} className="text-fg-faint" />
            <input
              autofocus
              value={query}
              onInput={(e) => setQuery(e.currentTarget.value)}
              placeholder="Buscar…"
              className="w-full border-0 bg-transparent text-13 outline-none placeholder:text-fg-faint"
            />
          </div>
        ) : null}
        <div className="max-h-72 overflow-auto py-0.5">
          {filtered.length === 0 ? (
            <p className="px-3 py-2.5 text-xs text-fg-muted">{emptyMessage}</p>
          ) : (
            sections.map((sec) => (
              <div key={sec.group}>
                {sec.group ? (
                    <p className="px-3 pb-1 pt-2 text-3xs font-semibold uppercase tracking-[0.16em] text-fg-faint">
                      {sec.group}
                    </p>
                  ) : null}
                {sec.items.map((opt) => {
                  const isSel = opt.value === value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      role="option"
                      aria-selected={isSel}
                      disabled={opt.disabled}
                      onClick={() => {
                        if (opt.disabled) return;
                        onChange(opt.value);
                        setOpen(false);
                      }}
                      className={cn(
                        'flex min-w-0 w-full items-center gap-2 rounded-md px-3 py-2 text-left text-13 outline-none transition-colors duration-med cursor-pointer',
                        opt.disabled && 'opacity-50 cursor-not-allowed',
                        !opt.disabled && 'hover:bg-surface-2 focus-visible:bg-surface-2',
                        isSel && 'bg-brand-tint text-brand',
                      )}
                    >
                      <span className="flex flex-1 flex-col leading-tight">
                        <span className="truncate">{opt.label}</span>
                        {opt.description ? (
                          <span className="truncate text-xs text-fg-muted">{opt.description}</span>
                        ) : null}
                      </span>
                      {isSel ? <Icon as={Check} size={14} /> : null}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
      </Popover>
    </>
  );
}
